/**
 * Lottie Animation Management for Tiles
 * Extends the existing lottie.js functionality with tile-specific optimizations
 */

// Import dotlottie player
import '../vendors/lottie/dotlottie.js';

( () => {

	'use strict';

	/**
	 * Configuration for different contexts
	 */
	const CONFIG = {
		tile: {
			intersectionThreshold: 0.3,
			rootMargin: '-10%',
			lazyLoad: true,
			priorityLoad: false,
		},
		hero: {
			intersectionThreshold: 0.1,
			rootMargin: '0px',
			lazyLoad: false,
			priorityLoad: true,
		},
		block: {
			intersectionThreshold: 0.5,
			rootMargin: '0px',
			lazyLoad: true,
			priorityLoad: false,
		},
	};

	/**
	 * Performance tracking
	 */
	const performanceTracker = {
		loadTimes: new Map(),
		errorCount: 0,

		/**
		 * Record load time for performance monitoring
		 *
		 * @param {string} id - Animation ID
		 * @param {number} startTime - Start timestamp
		 */
		recordLoadTime( id, startTime ) {

			const loadTime = performance.now() - startTime;
			this.loadTimes.set( id, loadTime );

			// Debug info in development
			if ( window.location.hostname === 'localhost' ) {

				// eslint-disable-next-line no-console
				console.log( `Lottie ${id} loaded in ${loadTime.toFixed( 2 )}ms` );

			}

		},

		/**
		 * Record loading error
		 */
		recordError() {

			this.errorCount += 1;

		},
	};

	/**
	 * Enhanced Lottie Player Manager
	 */
	class LottiePlayerManager {

		/**
		 * Constructor
		 */
		constructor() {

			this.players = new Map();
			this.observers = new Map();
			this.isMobile = window.matchMedia( '(max-width: 767px)' ).matches;
			this.isReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
			this.init();

		}

		/**
		 * Initialize the manager
		 */
		init() {

			this.findPlayers();
			this.setupEventListeners();

		}

		/**
		 * Find and register all Lottie animation containers
		 */
		findPlayers() {

			// Find tile-based Lottie animations
			const containers = document.querySelectorAll( '.c-lottie-animation' );

			containers.forEach( ( container ) => {

				const player = container.querySelector( 'dotlottie-player' );
				if ( player ) {

					this.registerPlayer( container, player );

				}

			} );

			// Find block-based Lottie animations
			this.findBlockAnimations();

		}

		/**
		 * Find and initialize Lottie animations in Gutenberg blocks
		 */
		findBlockAnimations() {

			const blockSelectors = [
				'.b-feature-block[data-lottie-enabled="true"]',
				'.b-statistics__item[data-lottie-enabled="true"]',
			];

			blockSelectors.forEach( ( selector ) => {

				const blocks = document.querySelectorAll( selector );

				blocks.forEach( ( block ) => {

					this.initializeBlockAnimation( block );

				} );

			} );

		}

		/**
		 * Initialize Lottie animation for a Gutenberg block
		 *
		 * @param {HTMLElement} block Block element with Lottie data attributes
		 */
		initializeBlockAnimation( block ) {

			if ( ! block.dataset.lottieJson ) {

				return;

			}

			const jsonUrl = block.dataset.lottieJson;
			const placeholderData = this.safeJsonParse( block.dataset.lottiePlaceholder, {} );
			const settings = this.safeJsonParse( block.dataset.lottieSettings, {} );

			// Find or create Lottie container within the block
			let container = block.querySelector( '.b-feature-block__lottie-container, .b-statistics__item__lottie-container' );

			if ( ! container ) {

				// Create container if it doesn't exist
				container = document.createElement( 'div' );
				container.className = 'lottie-block-container';
				container.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: -1; pointer-events: none;';
				block.style.position = 'relative';
				block.insertBefore( container, block.firstChild );

			}

			// Create dotlottie-player element
			const player = document.createElement( 'dotlottie-player' );
			player.className = 'c-lottie-animation__player';
			player.style.cssText = 'width: 100%; height: 100%;';

			// Set player attributes from settings
			player.dataset.src = jsonUrl;
			player.dataset.trigger = settings.trigger_type || 'scroll';
			player.dataset.speed = settings.animation_speed || '1';
			player.dataset.loop = settings.playback_mode === 'loop' ? 'true' : 'false';
			player.dataset.hoverEnd = settings.hover_end_action || 'rewind';

			if ( settings.background_enabled && settings.background_color ) {

				player.setAttribute( 'background', settings.background_color );

			} else {

				player.setAttribute( 'background', 'transparent' );

			}

			if ( settings.playback_mode === 'loop' ) {

				player.setAttribute( 'loop', '' );

			}

			if ( settings.show_controls ) {

				player.setAttribute( 'controls', '' );

			}

			// Add placeholder if available
			if ( placeholderData.url ) {

				const placeholder = document.createElement( 'img' );
				placeholder.src = placeholderData.url;
				placeholder.alt = placeholderData.alt || '';
				placeholder.className = 'c-lottie-animation__placeholder';
				placeholder.style.cssText = 'position: absolute; width: 100%; height: 100%; object-fit: cover; top: 0; left: 0;';
				container.appendChild( placeholder );

			}

			// Wrap container to match tile structure
			const wrapperContainer = document.createElement( 'div' );
			wrapperContainer.className = 'c-lottie-animation c-lottie-animation--block';
			wrapperContainer.id = `lottie-block-${  Date.now()}`;

			// Add player to wrapper
			wrapperContainer.appendChild( player );

			// Replace the original container with wrapper
			container.parentNode.replaceChild( wrapperContainer, container );

			// Register with the player manager
			this.registerPlayer( wrapperContainer, player );

		}

		/**
		 * Safely parse JSON string
		 *
		 * @param {string} jsonString JSON string to parse
		 * @param {*} defaultValue Default value if parsing fails
		 * @return {*} Parsed value or default
		 */
		safeJsonParse( jsonString, defaultValue = null ) {

			try {

				return JSON.parse( jsonString || 'null' ) || defaultValue;

			} catch ( error ) {

				return defaultValue;

			}

		}

		/**
		 * Register a player with the manager
		 *
		 * @param {HTMLElement} container - Container element
		 * @param {HTMLElement} player - Player element
		 */
		registerPlayer( container, player ) {

			const id = container.id || `lottie-${Date.now()}-${Math.random().toString( 36 ).substr( 2, 9 )}`;
			const context = this.getContext( container );
			const config = CONFIG[ context ] || CONFIG.tile;

			const playerData = {
				container,
				player,
				id,
				context,
				config,
				loaded: false,
				started: false,
				startTime: performance.now(),
			};

			this.players.set( id, playerData );
			this.setupPlayer( playerData );

		}

		/**
		 * Get context from container class
		 *
		 * @param {HTMLElement} container - Container element
		 * @return {string} Context name
		 */
		getContext( container ) {

			if ( container.classList.contains( 'c-lottie-animation--hero' ) ) {

				return 'hero';

			}
			if ( container.classList.contains( 'c-lottie-animation--block' ) ) {

				return 'block';

			}
			return 'tile';

		}

		/**
		 * Setup individual player
		 *
		 * @param {Object} playerData - Player data object
		 */
		setupPlayer( playerData ) {

			const { config } = playerData;

			// Skip if reduced motion is preferred
			if ( this.isReducedMotion ) {

				this.handleStaticDisplay( playerData );
				return;

			}

			// Setup loading observer
			this.setupLoadingObserver( playerData );

			// Setup play observer if needed
			if ( config.lazyLoad ) {

				this.setupPlayObserver( playerData );

			}

			// Setup hover interactions
			this.setupHoverInteractions( playerData );

			// Priority loading for important elements
			if ( config.priorityLoad ) {

				this.loadPlayer( playerData );

			}

		}

		/**
		 * Handle static display for reduced motion
		 *
		 * @param {Object} playerData - Player data object
		 */
		handleStaticDisplay( playerData ) {

			const { container, player } = playerData;
			const placeholder = container.querySelector( '.c-lottie-animation__placeholder' );

			// Hide player, show placeholder
			player.style.display = 'none';
			if ( placeholder ) {

				placeholder.style.opacity = '1';

			}

		}

		/**
		 * Setup loading observer
		 *
		 * @param {Object} playerData - Player data object
		 */
		setupLoadingObserver( playerData ) {

			const { container, config, id } = playerData;

			const observer = new IntersectionObserver(
				( entries ) => {

					entries.forEach( ( entry ) => {

						if ( entry.isIntersecting ) {

							this.loadPlayer( playerData );
							observer.unobserve( entry.target );

						}

					} );

				},
				{
					threshold: config.intersectionThreshold,
					rootMargin: config.rootMargin,
				}
			);

			observer.observe( container );
			this.observers.set( `loading-${id}`, observer );

		}

		/**
		 * Setup play observer
		 *
		 * @param {Object} playerData - Player data object
		 */
		setupPlayObserver( playerData ) {

			const { player, config, id } = playerData;
			const trigger = player.dataset.trigger || 'scroll';

			if ( trigger !== 'scroll' ) {

				return;

			}

			const observer = new IntersectionObserver(
				( entries ) => {

					entries.forEach( ( entry ) => {

						if ( entry.isIntersecting && playerData.loaded ) {

							this.playAnimation( playerData );

						}

					} );

				},
				{
					threshold: this.isMobile ? 0.2 : config.intersectionThreshold,
					rootMargin: this.isMobile ? '-20%' : config.rootMargin,
				}
			);

			this.observers.set( `play-${id}`, observer );

			// Observe after loading
			playerData.playObserver = observer;

		}

		/**
		 * Setup hover interactions
		 *
		 * @param {Object} playerData - Player data object
		 */
		setupHoverInteractions( playerData ) {

			const { container, player } = playerData;
			const trigger = player.dataset.trigger;

			if ( trigger !== 'hover' || this.isMobile ) {

				return;

			}

			const hoverEndAction = player.dataset.hoverEnd || 'rewind';

			// Find the hoverable element (could be tile container)
			const hoverElement = container.closest( '.c-post-box, .c-page-box, .c-news-box, .c-event-box' ) || container;

			hoverElement.addEventListener( 'mouseenter', () => {

				this.handleHoverStart( playerData );

			} );

			hoverElement.addEventListener( 'mouseleave', () => {

				this.handleHoverEnd( playerData, hoverEndAction );

			} );

		}

		/**
		 * Load player source
		 *
		 * @param {Object} playerData - Player data object
		 */
		loadPlayer( playerData ) {

			const { player, container } = playerData;
			const src = player.dataset.src || player.dataset.lottieSrc;

			if ( ! src || playerData.loaded ) {

				return;

			}

			// Check if player has load method
			if ( typeof player.load !== 'function' ) {

				this.onPlayerError( playerData );
				return;

			}

			// Add loading class
			container.classList.add( 'c-lottie-animation--loading' );

			try {

				// Load the animation source
				const loadResult = player.load( src );

				// Handle both Promise-based and event-based loading
				if ( loadResult && typeof loadResult.then === 'function' ) {

					// Promise-based loading
					loadResult.then( () => {

						this.onPlayerLoaded( playerData );

					} ).catch( () => {

						this.onPlayerError( playerData );

					} );

				} else {

					// Event-based loading - listen for ready event
					/**
					 * Handle ready event
					 */
					const handleReady = () => {

						player.removeEventListener( 'ready', handleReady );
						this.onPlayerLoaded( playerData );

					};

					/**
					 * Handle error event
					 */
					const handleError = () => {

						player.removeEventListener( 'error', handleError );
						this.onPlayerError( playerData );

					};

					player.addEventListener( 'ready', handleReady );
					player.addEventListener( 'error', handleError );

					// Fallback timeout in case neither event fires
					setTimeout( () => {

						player.removeEventListener( 'ready', handleReady );
						player.removeEventListener( 'error', handleError );

						// Check if player seems loaded
						if ( player.currentState !== 'error' ) {

							this.onPlayerLoaded( playerData );

						} else {

							this.onPlayerError( playerData );

						}

					}, 5000 );

				}

			} catch ( error ) {

				this.onPlayerError( playerData );

			}

		}

		/**
		 * Handle player loaded event
		 *
		 * @param {Object} playerData - Player data object
		 */
		onPlayerLoaded( playerData ) {

			const { player, container, id, playObserver } = playerData;

			playerData.loaded = true;

			// Remove loading state, add loaded state
			container.classList.remove( 'c-lottie-animation--loading' );
			container.classList.add( 'c-lottie-animation--loaded' );

			// Record performance
			performanceTracker.recordLoadTime( id, playerData.startTime );

			// Setup ready event
			player.addEventListener( 'ready', () => {

				this.onPlayerReady( playerData );

			} );

			// Setup play observer if it exists
			if ( playObserver ) {

				playObserver.observe( container );

			}

			// Auto-play for certain triggers
			const trigger = player.dataset.trigger;
			if ( trigger === 'auto' ) {

				setTimeout( () => this.playAnimation( playerData ), 100 );

			}

		}

		/**
		 * Handle player ready event
		 *
		 * @param {Object} playerData - Player data object
		 */
		onPlayerReady( playerData ) {

			const { player } = playerData;

			// Stop initial playback
			player.stop();

			// Remove placeholder with fade effect
			this.removePlaceholder( playerData );

		}

		/**
		 * Handle player error
		 *
		 * @param {Object} playerData - Player data object
		 */
		onPlayerError( playerData ) {

			const { container } = playerData;

			container.classList.remove( 'c-lottie-animation--loading' );
			container.classList.add( 'c-lottie-animation--error' );

			performanceTracker.recordError();

		}

		/**
		 * Remove placeholder with animation
		 *
		 * @param {Object} playerData - Player data object
		 */
		removePlaceholder( playerData ) {

			const { container } = playerData;
			const placeholder = container.querySelector( '.c-lottie-animation__placeholder' );

			if ( placeholder ) {

				// Use CSS transition for smooth removal
				placeholder.style.opacity = '0';
				setTimeout( () => {

					if ( placeholder.parentNode ) {

						placeholder.remove();

					}

				}, 300 );

			}

		}

		/**
		 * Play animation
		 *
		 * @param {Object} playerData - Player data object
		 */
		playAnimation( playerData ) {

			const { player } = playerData;

			if ( playerData.started ) {

				return;

			}

			playerData.started = true;

			// Apply settings via attributes (dotlottie-player approach)
			const speed = parseFloat( player.dataset.speed || 1 );
			const loop = player.dataset.loop === 'true' || player.hasAttribute( 'loop' );

			// Set speed attribute
			if ( speed !== 1 ) {

				player.setAttribute( 'speed', speed );

			}

			// Set loop attribute
			if ( loop ) {

				player.setAttribute( 'loop', '' );

			}

			// Set autoplay and play
			player.setAttribute( 'autoplay', 'true' );
			player.play();

		}

		/**
		 * Handle hover start
		 *
		 * @param {Object} playerData - Player data object
		 */
		handleHoverStart( playerData ) {

			const { player } = playerData;

			if ( ! playerData.loaded ) {

				return;

			}

			// Set direction attribute and play
			player.setAttribute( 'direction', '1' );
			player.play();

		}

		/**
		 * Handle hover end
		 *
		 * @param {Object} playerData - Player data object
		 * @param {string} action - Action to perform
		 */
		handleHoverEnd( playerData, action ) {

			const { player } = playerData;

			if ( ! playerData.loaded ) {

				return;

			}

			switch ( action ) {

			case 'stop':
				player.stop();
				break;
			case 'pause':
				player.pause();
				break;
			case 'rewind':
				player.setAttribute( 'direction', '-1' );
				player.play();
				break;
			default:
				// Do nothing
				break;

			}

		}

		/**
		 * Setup global event listeners
		 */
		setupEventListeners() {

			// Handle dynamic content loading
			const observer = new MutationObserver( ( mutations ) => {

				mutations.forEach( ( mutation ) => {

					mutation.addedNodes.forEach( ( node ) => {

						if ( node.nodeType === 1 ) { // Element node

							const containers = node.querySelectorAll ? node.querySelectorAll( '.c-lottie-animation' ) : [];
							containers.forEach( ( container ) => {

								const player = container.querySelector( 'dotlottie-player' );
								if ( player ) {

									this.registerPlayer( container, player );

								}

							} );

						}

					} );

				} );

			} );

			observer.observe( document.body, {
				childList: true,
				subtree: true,
			} );

			// Handle visibility changes for performance
			document.addEventListener( 'visibilitychange', () => {

				if ( document.hidden ) {

					this.pauseAllAnimations();

				} else {

					this.resumeAllAnimations();

				}

			} );

		}

		/**
		 * Pause all playing animations
		 */
		pauseAllAnimations() {

			this.players.forEach( ( playerData ) => {

				if ( playerData.loaded && playerData.started ) {

					playerData.player.pause();

				}

			} );

		}

		/**
		 * Resume all paused animations
		 */
		resumeAllAnimations() {

			this.players.forEach( ( playerData ) => {

				if ( playerData.loaded && playerData.started ) {

					playerData.player.play();

				}

			} );

		}

		/**
		 * Cleanup method for disposal
		 */
		dispose() {

			// Clean up observers
			this.observers.forEach( ( observer ) => {

				observer.disconnect();

			} );

			this.players.clear();
			this.observers.clear();

		}

	}

	/**
	 * Initialize manager when both DOM and custom elements are ready
	 */
	const initializeManager = () => {

		// Check if dotlottie-player is defined
		if ( ! customElements.get( 'dotlottie-player' ) ) {

			// Wait for custom element to be defined
			customElements.whenDefined( 'dotlottie-player' ).then( () => {

				new LottiePlayerManager();

			} );

		} else {

			new LottiePlayerManager();

		}

	};

	// Initialize when DOM is ready
	if ( document.readyState === 'loading' ) {

		document.addEventListener( 'DOMContentLoaded', initializeManager );

	} else {

		initializeManager();

	}

} )();